"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import type { StayExternalTrustLink } from "@/types/profile";
import {
  MAX_STAY_EXTERNAL_TRUST_LINKS,
  STAY_EXTERNAL_TRUST_SITES,
  STAY_EXTERNAL_TRUST_SITE_DOMAINS,
  stayExternalTrustUrlMatchesSite,
} from "@/lib/stays/stayHostTrust";
import { saveStayExternalTrustLinks } from "@/app/(public)/profile/[userId]/stay-external-trust-links/actions";

export type StayExternalTrustLinksFormProps = {
  userId: string;
  initialLinks: StayExternalTrustLink[];
  className?: string;
};

type DraftRow = { key: number; site: string; url: string; label: string };

const toDraft = (links: StayExternalTrustLink[]): DraftRow[] =>
  links.map((link, index) => ({
    key: index,
    site: link.site ?? "other",
    url: link.url ?? "",
    label: link.label ?? "",
  }));

/**
 * Badge 3 editor (2026-11-02): the host claims their external profiles —
 * Airbnb, Booking.com, Google Maps, LekkeSlaap, TravelGround, SafariNow,
 * Expedia, or any other site — so guests can tap through and read the reviews
 * exactly where they live. The claim stores a link and nothing else: we never
 * verify ownership of a linked profile, the public wording says "claimed", and
 * claims do not count toward "Verified host".
 */
export function StayExternalTrustLinksForm({ userId, initialLinks, className }: StayExternalTrustLinksFormProps) {
  const { toast } = useToast();
  const [rows, setRows] = useState<DraftRow[]>(toDraft(initialLinks));
  const [isSaving, setIsSaving] = useState(false);
  // Owner direction (2026-11-03): claiming is an attestation — the host ticks
  // that the profile they are linking is really theirs and really matches this
  // listing. Required before save; a link to someone else's profile is fraud
  // and is treated under the safety-report rules.
  const [attestedMatch, setAttestedMatch] = useState(false);

  // Live per-row feedback: a registry link must open on that site's own domain.
  const rowMismatches = useMemo(
    () =>
      rows.map(
        (row) =>
          row.url.trim().length > 0 && // only judge once something is typed
          /^https:\/\//i.test(row.url.trim()) &&
          !stayExternalTrustUrlMatchesSite(row.site, row.url.trim()),
      ),
    [rows],
  );
  const hasMismatch = rowMismatches.some(Boolean);

  const updateRow = (index: number, patch: Partial<DraftRow>) =>
    setRows((current) => current.map((candidate, i) => (i === index ? { ...candidate, ...patch } : candidate)));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSaving) return;
    if (rows.some((row) => row.url.trim()) && !attestedMatch) {
      toast({
        title: "Tick the declaration first",
        description: "Confirm the profiles you are linking are really yours and match this listing.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    try {
      const links = rows.map((row) => ({ site: row.site, url: row.url.trim(), label: row.label.trim() }));
      const result = await saveStayExternalTrustLinks({ userId, links });
      if (!result.success) {
        toast({ title: "Not saved", description: result.message || "Try again shortly.", variant: "destructive" });
        return;
      }
      setRows(toDraft(result.links));
      toast({ title: "Claimed profiles saved", description: result.message || "They now show on your listing." });
    } catch (error: any) {
      toast({
        title: "Not saved",
        description: error?.message || "Try again shortly.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const domainHintFor = (site: string): string => {
    const domains = STAY_EXTERNAL_TRUST_SITE_DOMAINS[site as StayExternalTrustLink["site"]] ?? [];
    return domains.length > 0 ? `Must be a link on ${domains.map((d) => d).join(" or ")}` : "Any https:// link — the full address shows on your listing";
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Paste the links where your past guests left reviews — the biggest scam-killer there is. These show on
          your listing as <span className="font-medium text-foreground">claimed</span> links; guests tap through
          and read the reviews on the other site themselves. Up to {MAX_STAY_EXTERNAL_TRUST_LINKS}.
          <span className="mt-1 block font-medium text-foreground">
            Only link your OWN profiles. Each link must open on that site&apos;s own web address — a
            &ldquo;Airbnb profile&rdquo; that is not on airbnb.com is dropped. Claiming someone else&apos;s
            profile is fraud and reports of it freeze listings same-day.
          </span>
        </p>
        {rows.map((row, index) => {
          const mismatch = rowMismatches[index];
          return (
            <div key={row.key} className="rounded-lg border bg-muted/20 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={row.site}
                  aria-label="Which site"
                  onChange={(event) => updateRow(index, { site: event.target.value })}
                  className="h-10 w-44 rounded-md border bg-background px-3 text-sm text-foreground"
                >
                  {STAY_EXTERNAL_TRUST_SITES.map((site) => (
                    <option key={site.key} value={site.key}>
                      {site.label}
                    </option>
                  ))}
                </select>
                <Input
                  value={row.url}
                  aria-label="Profile URL"
                  onChange={(event) => updateRow(index, { url: event.target.value })}
                  placeholder="https://..."
                  className="min-w-0 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label="Remove this claimed link"
                  onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                >
                  ✕
                </Button>
              </div>
              <p className={`text-[11px] ${mismatch ? "font-semibold text-destructive" : "text-muted-foreground"}`}>
                {mismatch
                  ? "This link does not open on that site's own web address — fix it or choose “Other site”."
                  : domainHintFor(row.site)}
              </p>
              {row.site === "other" && (
                <Input
                  value={row.label}
                  aria-label="Site name for the other link"
                  onChange={(event) => updateRow(index, { label: event.target.value })}
                  placeholder="What should this link be called? (e.g. Vrbo profile)"
                />
              )}
            </div>
          );
        })}
        <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-3 text-xs leading-relaxed">
          <Checkbox
            checked={attestedMatch}
            onCheckedChange={(value) => setAttestedMatch(value === true)}
            className="mt-0.5"
          />
          <span>
            <span className="font-semibold text-foreground">Declaration:</span> these profiles are mine, the
            name and photo on them match my Zamzi listing, and I understand a claimed link is shown to guests
            as <span className="font-medium">claimed</span> — never as verified — and claiming a profile that
            is not mine is fraud.
          </span>
        </label>
        <div className="flex flex-wrap gap-2">
          {rows.length < MAX_STAY_EXTERNAL_TRUST_LINKS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setRows((current) => [...current, { key: Date.now(), site: "airbnb", url: "", label: "" }])
              }
            >
              + Add a claimed profile
            </Button>
          )}
          <Button type="submit" size="sm" disabled={isSaving || hasMismatch}>
            {isSaving ? "Saving…" : "Save claimed profiles"}
          </Button>
        </div>
        {hasMismatch && (
          <p className="text-xs font-medium text-destructive">
            Fix the highlighted link(s) — a claimed site must open on that site&apos;s own web address — or save
            them under &ldquo;Other site&rdquo;.
          </p>
        )}
      </div>
    </form>
  );
}
